import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { useClickAway } from 'react-use';
import { Sticky } from 'react-sticky';
import Link from 'components/shared/link';

import styles from './table-of-content.module.scss';
import CloseIcon from './svg/close.inline.svg';
import ListIcon from './svg/list.inline.svg';

const cx = classNames.bind(styles);

const HEADER_CLASS_NAME = 'toc-header';

const TableOfContent = ({ title, contentClassName, extraLinks, useCustomTitleOnly }) => {
  const stickyRef = useRef();
  const dropdownWrapper = useRef(null);

  const [items, setItems] = useState([]);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  useClickAway(dropdownWrapper, () => {
    setIsOpenDropdown(false);
  });

  const getId = (title) => {
    const id = title.charAt(0).toLowerCase() + title.slice(1).replace(/[\s.:,?!%]/g, '');
    return id;
  };

  const getStickyPositionRight = (style) => {
    if (typeof window !== 'undefined' && stickyRef.current) {
      const mediaQuery = window.matchMedia('(max-width: 1439.98px)');
      const width = stickyRef.current.content.clientWidth;

      const position = mediaQuery.matches ? style.left : style.left - (width + 40) / 2;

      return `${position}px`;
    }

    return `${style.left}px`;
  };

  const handleOpenDropdown = () => setIsOpenDropdown(!isOpenDropdown);

  useEffect(() => {
    const content = document.querySelector(`.${contentClassName}`);
    const headings = content.querySelectorAll(`.${HEADER_CLASS_NAME}`);
    const items = [];

    headings.forEach((heading) => {
      const title = heading.innerText.trim();
      const id = getId(title);
      heading.setAttribute('id', id);
      items.push(title);
    });

    setItems(items);
  }, []);

  const getAnchors = () => (
    <ul className={cx('list')}>
      {items.map((item, index) => {
        const href = getId(item);
        const active = window.location.href.split('#')[1] === href;
        return (
          <li className={cx('item')} key={index}>
            <a className={cx({ active })} href={`#${href}`}>
              {item}
            </a>
          </li>
        );
      })}
    </ul>
  );

  const getExtraLinks = () => (
    <ul className={cx('list')}>
      {extraLinks.map(({ linkText, url }, index) => (
        <li className={cx('item')} key={index}>
          <Link to={url}>{linkText}</Link>
        </li>
      ))}
    </ul>
  );

  const getContent = () => (
    <>
      {items.length > 0 && (
        <div>
          <span className={cx('title')}>{title}</span>
          {getAnchors()}
        </div>
      )}

      {extraLinks && (
        <div>
          <span className={cx('title')}>{useCustomTitleOnly ? title : 'Also read'}</span>
          {getExtraLinks()}
        </div>
      )}
    </>
  );

  return (
    <Sticky disableCompensation ref={stickyRef} topOffset={-140}>
      {({ style, isSticky }) => {
        const positionRight = getStickyPositionRight(style);
        return (
          <div
            className={cx('wrapper', { isNotSticky: !isSticky })}
            style={{ right: positionRight, ...style, top: isSticky ? 120 : -20 }}
          >
            <div className={cx('list-wrapper')}>{getContent()}</div>
            <div className={cx('dropdown-wrapper')} ref={dropdownWrapper}>
              <button
                className={cx('dropdown-button')}
                onClick={handleOpenDropdown}
                onKeyDown={handleOpenDropdown}
                type="button"
              >
                {!isOpenDropdown ? <ListIcon /> : <CloseIcon />}
                <div className={cx('dropdown', { active: isOpenDropdown })}>{getContent()}</div>
              </button>
            </div>
          </div>
        );
      }}
    </Sticky>
  );
};

TableOfContent.defaultProps = {
  title: 'Table of contents',
  useCustomTitleOnly: false,
  extraLinks: null,
};

TableOfContent.propTypes = {
  title: PropTypes.string,
  contentClassName: PropTypes.string.isRequired,
  useCustomTitleOnly: PropTypes.bool,
  extraLinks: PropTypes.arrayOf(
    PropTypes.shape({ linkText: PropTypes.string, url: PropTypes.string })
  ),
};

export default TableOfContent;
